import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    useHistory,
    Link,
} from "react-router-dom";
import { PlusCircleIcon, MinusCircleIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import {
    Form,
    FormGroup,
    TextInput,
    TextArea,
    ActionGroup,
    Button,
    FormAlert,
    Alert,
    Popover,
    List,
    ListItem,
    Modal,
    ModalVariant,
    CodeBlockCode,
    CodeBlock,
} from '@patternfly/react-core';
import { ResourceItem, ResourceItemVariants, ResourceItemType } from '@app/components/ResourceItem';
import { SecretWithValue } from '../../../../../shared/secretmanager/changerequests/types';
import { useCreateChangeRequest } from '../../hooks/useSecretManagerData';
import { ApiError } from '@app/hooks/api';
import { ValidateDescription, ValidateJiraTicket, ValidateSecretName, ValidateSecretValue, ValidateVault } from '../../../../../shared/secretmanager/changerequests/validations';

interface ChangeRequestCreationPageProps {
    root: string;
}



interface SecretInputProps {
    id: number;
    value?: SecretWithValue;
    onChange?: (id: number, field: string, values: SecretWithValue) => void;
    submitted: boolean;
}

const SecretFormInput: React.FunctionComponent<SecretInputProps> = (props: SecretInputProps) => {
    const [keyvault, setKeyvault] = useState<string>(props.value?.vault || '');
    const [secret, setSecret] = useState<string>(props.value?.name || '');
    const [value, setValue] = useState<string>(props.value?.value || '');

    useEffect(() => {
        setKeyvault(props.value?.vault || '');
        setSecret(props.value?.name || '');
        setValue(props.value?.value || '');
    }, [props.value]);

    function raise_change(field: string) {
        props.onChange?.(props.id, field, { vault: keyvault, name: secret, value });
    }


    return (
        <div className="dx-grid dx-grid--gutter" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <FormGroup>
                <TextInput
                    isRequired
                    type="text"
                    id={`form-secret-${props.id}-keyvault`}
                    name={`form-secret-${props.id}-keyvault`}
                    value={keyvault}
                    onChange={(_, value) => setKeyvault(value)}
                    onBlur={() => raise_change('keyvault')}
                    placeholder='Keyvault name (eg: kv-iac-tst-pr-eu2-01)'
                />
            </FormGroup>
            <FormGroup>

                <TextInput
                    isRequired
                    type="text"
                    id={`form-secret-${props.id}-secret`}
                    name={`form-secret-${props.id}-secret`}
                    value={secret}
                    onChange={(_, value) => setSecret(value)}
                    onBlur={() => raise_change('secret')}
                    placeholder='Secret name (eg: my-secret-key)'
                />
            </FormGroup>
            <FormGroup>
                <TextArea
                    isRequired
                    type="text"
                    id={`form-secret-${props.id}-value`}
                    name={`form-secret-${props.id}-value`}
                    style={{whiteSpace: 'nowrap'}}
                    value={value}
                    onChange={(_, value) => setValue(value)}
                    onBlur={() => raise_change('value')}
                    placeholder='Secret value (any content)'
                />
            </FormGroup>

        </div>
    );
};

interface SecretListInputProps {
    values: SecretWithValue[];
    onChange: (values: SecretWithValue[]) => void;
    submitted: boolean;
    errors: any[];
    onErrorsCleared: (key: string) => void;
}

const SecretListInput: React.FunctionComponent<SecretListInputProps> = (props: SecretListInputProps) => {

    const [errors, setErrors] = useState<any[]>(props.errors);

    useEffect(() => {
        setErrors(props.errors)
    }, [props.errors])

    function update_secret(id: number, updated_field: string, value: SecretWithValue) {
        const cur_value = props.values[id];
        if (cur_value.vault !== value.vault || cur_value.name !== value.name || cur_value.value !== value.value) {
            const secret_error_pos = errors.findIndex(el => el.id === id)
            if (secret_error_pos >= 0) {
                const secret_error = errors[secret_error_pos];

                const remaining_errors = secret_error.errors.filter(({ field, _ }) => field !== updated_field);
                if (remaining_errors.length > 0) {
                    errors[secret_error_pos].errors = remaining_errors;
                } else {
                    errors.splice(secret_error_pos, 1);
                }

                setErrors([...errors])
                if (errors.length == 0) {
                    props.onErrorsCleared('secrets');
                }
            }
        }

        const elements = props.values;
        elements[id] = value;
        props.onChange([...elements]);
    }

    function create_secret() {
        props.onChange([...props.values, { vault: '', name: '', value: '' }]);
    }

    function remove_secret(id: number) {
        const values = props.values;
        values.splice(id, 1);
        props.onChange([...values]);

        const error_id = errors.findIndex(el => el.id === id);
        if (error_id >= 0) {
            errors.splice(error_id, 1);
            setErrors([...errors])
            if (errors.length == 0) {
                props.onErrorsCleared('secrets');
            }
        }
    }

    return (
        <>
            {props.values?.map((value: SecretWithValue, index) => {
                const secret_errors = errors.find(({ id, errors }) => id === index);
                const messages = secret_errors?.errors.map(({ _, message }) => message)

                return <div style={{ paddingTop: 4, paddingBottom: 4 }}>
                    <div className="dx-split" key={index}>
                        <div className="dx-split-item--fill">
                            <SecretFormInput id={index} value={value} onChange={update_secret} submitted={props.submitted} />
                        </div>
                        <div>
                            <Button variant="link" icon={<MinusCircleIcon />} isDisabled={props.values.length === 1} onClick={() => remove_secret(index)} />
                        </div>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                <span style={{ width: '24px', display: 'inline-block', textAlign: 'center' }}>

                                    {messages?.length > 0 && <Popover
                                        triggerAction="hover"
                                        position='right-end'
                                        alertSeverityVariant={'danger'}
                                        headerIcon={<ExclamationCircleIcon />}
                                        aria-label="Hoverable popover"
                                        headerContent={'Secret errors'
                                        } bodyContent={
                                            <div>
                                                <List>
                                                    {messages?.map(message => <ListItem>{message}</ListItem>)}
                                                </List>
                                            </div>
                                        }
                                        footerContent={undefined}
                                    >
                                        <span className="dx-icon dx-icon--inline dx-icon--md dx-icon--danger"><ExclamationCircleIcon /></span>
                                    </Popover>
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            })}
            <Button variant="link" onClick={create_secret} isDisabled={props.values.length >= 10} icon={<PlusCircleIcon /> }>
                Add new secret
            </Button>
            {errors.length > 0 && (
                <div className="dx-helper-text dx-helper-text--error">
                    The secrets contain errors. Please check them before continuing.
                </div>
            )}
        </>
    );
};

enum AlertSeverity {
    OK = 'success',
    WARNING = 'warning',
    ERROR = 'danger'
}

interface Alert {
    severity: AlertSeverity
    title: string,
    message: string | React.ReactNode,
}

export const AlertModal: React.FunctionComponent<{ alert: Alert | undefined }> = ({ alert }) => {

    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        setVisible(true);
    }, [alert])

    if (!alert) {
        return <></>
    }

    return (
        <Modal
            variant={ModalVariant.medium}
            title={alert.title}
            isOpen={visible}
            titleIconVariant={alert.severity}
            onClose={() => { setVisible(false) }}
            showClose={false}
            actions={[
                <Button key="confirm" variant="primary" onClick={() => { setVisible(false) }}>
                    Accept
                </Button>
            ]}
        >
            {alert.message}
        </Modal>
    )
}

export const ChangeRequestCreationPage: React.FunctionComponent<ChangeRequestCreationPageProps> = ({ root }) => {
    const [ticket, setTicket] = useState('');
    const [description, setDescription] = useState('');
    const [secrets, setSecrets] = useState<SecretWithValue[]>([{} as SecretWithValue]);
    const history = useHistory();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [secretErrors, setSecretErrors] = useState<{}[]>([]);
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [alert, setAlert] = useState<Alert>();

    function validateForm() {
        let isValid = true;

        formErrors.splice(0, formErrors.length);

        if (ValidateJiraTicket(ticket).length > 0) {
            isValid = false;
            formErrors.push('ticket')
        }

        if (ValidateDescription(description).length > 0) {
            isValid = false;
            formErrors.push('description')
        }

        const secrets_errors: { id: number, errors: any[] }[] = [];

        for (let id = 0; id < secrets.length; id++) {
            const secret = secrets[id];
            const secret_errors: { field: string, message: string }[] = []

            if (ValidateVault(secret.vault).length > 0) {
                secret_errors.push({
                    field: 'keyvault',
                    message: 'Keyvault name is not valid. It can only contain letters, numbers and dashes (-). From 3 to 24 characters. Eg: kv-iac-tst-pr-eu2-01'
                })
            }

            if (ValidateSecretName(secret.name).length > 0) {
                secret_errors.push({
                    field: 'secret',
                    message: 'Secret name is not valid. It can only contain letters, numbers and dashes (-). From 1 to 127 characters. Eg: my-secret-key'
                })
            }

            if (ValidateSecretValue(secret.value).length > 0) {
                secret_errors.push({
                    field: 'value',
                    message: 'Secret value is required.'
                })
            }

            if (secret_errors.length > 0) {
                secrets_errors.push({ id: id, errors: secret_errors });
            }

        }

        if (secrets_errors.length > 0) {
            setSecretErrors(secrets_errors);
            isValid = false;
            formErrors.push('secrets')
        }

        setFormErrors(formErrors);
        return isValid;
    }

    function onErrorsCleared(key: string) {
        const index = formErrors.indexOf(key);
        if (index >= 0) {
            formErrors.splice(index, 1);
            setFormErrors(formErrors);

            if (formErrors.length == 0) {
                setError('');
            }
        }

    }

    const createMutation = useCreateChangeRequest();

    async function submit_form() {
        setSubmitted(true);

        if (!validateForm()) {
            setError('Form has errors. Please correct them before submitting.');
            return;
        }

        setError('');
        setSubmitting(true);

        try {
            const result = await createMutation.mutateAsync({ ticket, description, secrets });
            const destination = `${root}/view/${result.id}`;
            window.location.replace(destination);
        } catch (err) {
            const apiError = err as ApiError;
            const alert: Alert = {
                severity: AlertSeverity.ERROR,
                title: 'There were errors..',
                message: (
                    <CodeBlock>
                        <CodeBlockCode>
                            {JSON.stringify(apiError.errors || [{ message: apiError.message }], null, 2)}
                        </CodeBlockCode>
                    </CodeBlock>
                )
            }
            setAlert(alert)
        }
        setSubmitting(false);
    }

    return (
        <>
            <AlertModal alert={alert} />
            <section className="dx-page-section">
                <nav className="dx-breadcrumb">
                    <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}`}>Change requests</Link></span>
                </nav>
                <div className="dx-split dx-split--gutter" style={{ flexWrap: 'wrap' }}>
                    <div className="dx-split-item--fill">
                        <ResourceItem variant={ResourceItemVariants.h1} type={ResourceItemType.ChangeRequest}>New change request</ResourceItem>
                    </div>
                </div>
            </section>
            <hr className="dx-divider" />
            <section className="dx-page-section">
                <Form>
                    <FormGroup label="Ticket" isRequired>
                        <TextInput
                            isRequired
                            type="text"
                            id="form-ticket"
                            name="form-ticket"
                            value={ticket}
                            placeholder='Jira ticket, as CHG-XXXX | ARAPS-XXXX | DEVSD-XXXX'
                            onChange={(_, value) => { setTicket(value.toUpperCase()); onErrorsCleared('ticket') }}
                            validated={submitted && ValidateJiraTicket(ticket).length > 0 ? 'error' : 'default'}
                        />
                        {submitted && ValidateJiraTicket(ticket).length > 0 && (
                            <div className="dx-helper-text dx-helper-text--error">
                                Ticket is not valid. Must be between 2-10 characters, following a dash, then up to 7 numbers. Example: RELEASE-123
                            </div>
                        )}
                    </FormGroup>
                    <FormGroup label="Description" isRequired>
                        <TextInput
                            isRequired
                            id="form-description"
                            name="form-description"
                            value={description}
                            placeholder='Describe the reason for change using 10-72 characters'
                            onChange={(_, value) => { setDescription(value); onErrorsCleared('description') }}
                            validated={submitted && ValidateDescription(description).length > 0 ? 'error' : 'default'}
                        />
                        {submitted && ValidateDescription(description).length > 0 && (
                            <div className="dx-helper-text dx-helper-text--error">
                                Description is not valid. Must be between 10 and 72 characters.
                            </div>
                        )}
                    </FormGroup>
                    <FormGroup label="Secrets" isRequired>
                        <SecretListInput onErrorsCleared={onErrorsCleared} values={secrets} onChange={setSecrets} submitted={submitted} errors={secretErrors} />
                    </FormGroup>
                    <ActionGroup>
                        <Button variant="primary" onClick={submit_form} isDisabled={submitting}>Submit</Button>
                        <Button variant="link" onClick={history.goBack}>Cancel</Button>
                    </ActionGroup>
                    {error && (
                        <FormAlert>
                            <Alert variant="danger" title={error} />
                        </FormAlert>
                    )}
                </Form>
            </section>
        </>
    );
};
